import TruncatedTypography from '@components/TruncatedTypography';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

interface Props<T extends string | number> {
  steps: {
    label: string;
    value: T;
  }[];
  value: T;
  onChange?: (value: T) => void;
}

export const Stepper = <T extends string | number>({ steps, value, onChange }: Props<T>) => {
  const activeIndex = steps.findIndex((step) => step.value === value);
  const activeStepRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (activeStepRef.current) {
      activeStepRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [activeIndex]);

  return (
    <Box
      display="grid"
      gridTemplateColumns={`repeat(${steps.length}, 1fr)`}
      m="-1px"
      bgcolor={(theme) => theme.palette.background.paper}
      sx={{ overflowX: 'hidden' }}
    >
      {steps.map((step, idx) => {
        const active = value === step.value;

        return (
          <ButtonBase
            ref={active ? activeStepRef : null}
            disabled={!onChange}
            key={step.value}
            onClick={onChange ? () => onChange(step.value) : undefined}
            sx={{
              py: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: idx > activeIndex ? 'text.secondary' : undefined,
              borderWidth: active ? '1px 1px 0 1px' : '0 0 1px 0 ',
              borderColor: 'white',
              borderStyle: 'solid',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            }}
          >
            <Box
              width={22}
              height={22}
              borderRadius={20}
              bgcolor={idx > activeIndex ? 'text.secondary' : 'white'}
              sx={{
                color: 'black',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Typography fontWeight="medium" lineHeight={1} fontSize={14}>
                {idx + 1}
              </Typography>
            </Box>
            <TruncatedTypography fontWeight="medium">{step.label}</TruncatedTypography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};
